import { Form } from "react-bootstrap";
import {
  Control,
  Controller,
  FieldErrors,
  UseFormRegister,
  UseFormWatch
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { BiSolidVolumeFull } from "react-icons/bi";

import { useAppDispatch, useAppSelector } from "../../app/hook";
import { useGetMessageSoundsQuery } from "../../app/services/chatbot-rtk.services";
import PopoverWithAudio, {
  IPopoverWithAudioType
} from "../../components/common/PopoverWithAudio";
import ProBadge from "../../components/ui/ProBadge";
import usePermission from "../../hooks/usePermission";
import Utilities from "../../utils";
import {
  handleResetAppearance,
  handleUpdateAppearanceItem,
  selectAppearanceState
} from "./appearanceSlice";
import { IAiAppearance } from "./types";
import React from "react";

type Props = {
  register: UseFormRegister<IAiAppearance>;
  watch: UseFormWatch<IAiAppearance>;
  errors: FieldErrors<IAiAppearance>;
  control: Control<IAiAppearance, any>;
};

function AppearanceMessageSound({ register, watch, errors, control }: Props) {
  const permissions = usePermission();
  const { i18n, t } = useTranslation();
  const dispatch = useAppDispatch();
  const { configs, originalData } = useAppSelector(selectAppearanceState);
  const { data, isSuccess, isLoading } = useGetMessageSoundsQuery(undefined, {
    refetchOnMountOrArgChange: true
  });

  const voiceResponsePopData: IPopoverWithAudioType = {
    audioUrl: Utilities.VoiceNotificationSoundAudioLinks[i18n.language],
    contentText: t("DashboardSettingCustomizeChat/pop-15")
  };

  const handlePlaySelectedSound = () => {
    if (!watch("notificationSound")) {
      return;
    }

    const audio = new Audio(watch("notificationSound"));

    audio.play();
  };

  return (
    <React.Fragment>
      <div
        className={
          "tw-self-stretch tw-flex-col tw-justify-start tw-items-start tw-gap-3 tw-flex"
        }
      >
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
          <Form.Group className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
            <Form.Check
              disabled={
                !permissions.settingsPage.widget.customization.notificationBadge
              }
              type="switch"
              id="isNotificationSoundActive"
              className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight"
              label={t("DashboardSettingCustomizeChat/l-18")}
              {...register("isNotificationSoundActive", {
                onChange: (e: any) => {
                  dispatch(
                    handleUpdateAppearanceItem({
                      key: "isNotificationSoundActive",
                      value: e.target.checked
                    })
                  );
                }
              })}
            />
            {!permissions.settingsPage.widget.customization
              .notificationBadge && (
              <ProBadge
                className="tw-ms-2"
                onClose={() => {
                  dispatch(handleResetAppearance());
                }}
              />
            )}
            <PopoverWithAudio
              data={{
                contentText: voiceResponsePopData.contentText,
                audioUrl: voiceResponsePopData.audioUrl
              }}
            />
          </Form.Group>
        </div>
      </div>
      {watch("isNotificationSoundActive") && (
        <div className="tw-self-stretch tw-justify-start tw-items-start tw-gap-6 tw-flex tw-w-full tw-flex-col">
          <Form.Group className="tw-w-full tw-col-span-12">
            <Form.Label className="tw-text-gray-500 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
              Sound
            </Form.Label>
            <div className="tw-flex tw-items-center tw-gap-4">
              <Controller
                control={control}
                name="notificationSound"
                render={({ field: { onChange, value } }) => (
                  <Form.Select
                    disabled={
                      isLoading ||
                      !permissions.settingsPage.widget.customization
                        .notificationSound
                    }
                    value={(configs as any).notificationSound}
                    onChange={(e) => {
                      onChange(e.target.value);

                      dispatch(
                        handleUpdateAppearanceItem({
                          key: "notificationSound",
                          value: e.target.value
                        })
                      );
                    }}
                  >
                    <option value={""}>
                      {t("DashboardSettingCustomizeChat/choose")}
                    </option>
                    {data?.map((sound) => (
                      <option key={sound.title} value={sound.cdnPath}>
                        {sound.title}
                      </option>
                    ))}
                  </Form.Select>
                )}
              />
              <button
                type="button"
                className="tw-w-6 tw-h-6 tw-text-blue-500 disabled:tw-text-gray-400 tw-bg-transparent tw-ring-0 tw-border-none"
                disabled={
                  !watch("notificationSound") ||
                  !permissions.settingsPage.widget.customization
                    .notificationSound
                }
                onClick={handlePlaySelectedSound}
              >
                <BiSolidVolumeFull size={22} />
              </button>

              {!permissions.settingsPage.widget.customization
                .notificationSound && (
                <ProBadge
                  className="tw-ms-2"
                  onClose={() => {
                    dispatch(handleResetAppearance());
                  }}
                />
              )}
            </div>
          </Form.Group>
        </div>
      )}
    </React.Fragment>
  );
}

export default AppearanceMessageSound;
